/* Darkside used copies, V1.1. */
(() => {
  'use strict';

  if (customElements.get('darkside-used-copies')) return;

  function normalize(value) {
    return value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()
      .replace(/[’'`]/g, '').replace(/&/g, ' and ')
      .replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  }

  function withoutParentheses(value) {
    let previous;

    do {
      previous = value;
      value = value.replace(/\([^()]*\)/g, ' ');
    } while (value !== previous);

    return /[()]/.test(value) ? '' : value;
  }

  function parseTitle(value) {
    const separator = /-\s+/.exec(value);
    if (!separator) return null;

    const rawArtist = normalize(value.slice(0, separator.index));
    const artist = rawArtist === 'rage against machine'
      ? 'rage against the machine'
      : rawArtist;

    const album = normalize(
      withoutParentheses(
        value.slice(separator.index + separator[0].length)
      )
    );

    return artist && album ? { artist, album } : null;
  }

  function matches(source, candidate, format, sourceId) {
    const title = parseTitle(candidate.title || '');

    return !!(
      title &&
      source &&
      candidate.format === format &&
      candidate.inStock === true &&
      String(candidate.id) !== String(sourceId) &&
      title.artist === source.artist &&
      title.album === source.album
    );
  }

  function localURL(value) {
    const url = new URL(value, location.origin);

    if (
      url.origin !== location.origin ||
      !/^https?:$/.test(url.protocol)
    ) {
      throw new Error('Unexpected product URL');
    }

    return url;
  }

  class UsedCopies extends HTMLElement {
    connectedCallback() {
      if (this.started) return;

      this.started = true;
      this.controller = new AbortController();

      this.load().catch((error) => {
        if (this.dataset.designMode === 'true') {
          console.warn('[Darkside used copies]', error.message);
        }
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.started = false;
    }

    async load() {
      const source = parseTitle(this.dataset.title || '');
      if (!source) return;

      const signal = this.controller.signal;
      const timeout = setTimeout(
        () => this.controller.abort(),
        12000
      );

      const found = new Map();

      // Search broadly, then check artist, album, format and stock.
      const words = [
        ...new Set(
          (source.artist + ' ' + source.album).split(' ')
        )
      ];

      const query = words.join(' ');
      const base = localURL(this.dataset.searchUrl);

      base.searchParams.set('view', 'darkside-used-copies');
      base.searchParams.set('type', 'product');
      base.searchParams.set('q', query);
      base.searchParams.set('options[prefix]', 'none');
      base.searchParams.set('options[unavailable_products]', 'hide');

      // Preserve the preview theme.
      const preview = new URL(location.href)
        .searchParams.get('preview_theme_id');

      if (preview || this.dataset.designMode === 'true') {
        base.searchParams.set(
          'preview_theme_id',
          preview || this.dataset.themeId
        );
      }

      try {
        let page = 1;

        while (page && page <= 4 && !signal.aborted) {
          const url = new URL(base);
          url.searchParams.set('page', String(page));

          const response = await fetch(url, {
            credentials: 'same-origin',
            cache: 'no-store',
            signal
          });

          if (!response.ok) {
            throw new Error(
              'Search request failed: ' + response.status
            );
          }

          const data = await response.json();

          if (
            data.version !== 1 ||
            !Array.isArray(data.products)
          ) {
            throw new Error(
              'Used-copy search template unavailable'
            );
          }

          for (const candidate of data.products) {
            if (
              matches(
                source,
                candidate,
                this.dataset.format,
                this.dataset.productId
              )
            ) {
              found.set(String(candidate.id), candidate);
            }
          }

          if (found.size) {
            this.render([...found.values()]);
          }

          const next = data.nextPage;
          page = Number.isInteger(next) && next > page
            ? next
            : null;
        }

        if (page && this.dataset.designMode === 'true') {
          console.warn(
            '[Darkside used copies] Search reached its four-page limit; more copies may exist.'
          );
        }
      } finally {
        clearTimeout(timeout);
      }
    }

    render(products) {
      if (!this.isConnected) return;

      const list = this.querySelector('.ds-used-copies__list');
      const fragment = document.createDocumentFragment();

      products.sort(
        (a, b) =>
          a.price - b.price ||
          String(a.title).localeCompare(String(b.title))
      );

      for (const product of products) {
        const item = document.createElement('li');
        const link = document.createElement('a');

        link.className = 'ds-used-copies__link';

        const url = localURL(product.url);
        const preview = new URL(location.href)
          .searchParams.get('preview_theme_id');

        if (preview) {
          url.searchParams.set('preview_theme_id', preview);
        }

        link.href = url.pathname + url.search;

        if (product.image) {
          const imageURL = new URL(
            product.image,
            location.origin
          );

          if (
            imageURL.protocol === 'https:' ||
            imageURL.protocol === 'http:'
          ) {
            const image = document.createElement('img');

            image.className = 'ds-used-copies__image';
            image.src = imageURL.href;
            image.alt = '';
            image.width = 64;
            image.height = 64;
            image.loading = 'lazy';

            link.append(image);
          }
        }

        const details = document.createElement('span');
        details.className = 'ds-used-copies__details';

        const fields = [
          ['title', product.title],
          ['meta', product.format + ' · ' + product.priceText],
          ['cta', 'View used copy']
        ];

        for (const [kind, text] of fields) {
          const span = document.createElement('span');

          span.className = 'ds-used-copies__' + kind;
          span.textContent = text;

          details.append(span);
        }

        link.append(details);
        item.append(link);
        fragment.append(item);
      }

      list.replaceChildren(fragment);
      this.hidden = products.length === 0;
    }
  }

  customElements.define('darkside-used-copies', UsedCopies);
})();